import React from 'react';
import Modal from './Modal';

export type EditorChromeVariant = 'modal' | 'panel';

export interface EditorChromeProps {
  variant?: EditorChromeVariant;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  showClose?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  /** Hide Cancel-style chrome in panel mode; keep save actions in footer. */
  panelClassName?: string;
}

/**
 * Shared chrome for owner editors used both as standalone dialogs and as
 * inline panels inside Manage build.
 */
const EditorChrome: React.FC<EditorChromeProps> = ({
  variant = 'modal',
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
  className = '',
  showClose = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  panelClassName = '',
}) => {
  if (!isOpen) return null;

  if (variant === 'panel') {
    return (
      <div className={`space-y-4 ${panelClassName}`.trim()}>
        <div className="sr-only">
          <h4>{title}</h4>
          {description ? <p>{description}</p> : null}
        </div>
        {children}
        {footer != null ? (
          <div className="pt-4 border-t border-white/[0.06] space-y-2">
            <p className="text-xs text-gray-500">Saves only this block.</p>
            {footer}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      size={size}
      footer={footer}
      className={className}
      showClose={showClose}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
    >
      {children}
    </Modal>
  );
};

export default EditorChrome;
